/**
 * src/components/directory-page/FeaturedSection.js
 *
 * Sección "Negocios destacados - Plan Oro".
 * Fetchea /data/businesses.json directamente para reflejar cambios del admin.
 */

import { define, c } from '@components/Component.js'
import { h } from '@core/dom.js'
import { signal, effect } from '@core/signal.js'
import { directoryData } from '@/db/directory.db.js'
import { uiIcons } from '@/shared/brandIcons.js'
import { getReviewStats, getBusinessStats } from '@/services/reviewStats.js'

const isActive = b => {
    if (b.enabled === false) return false
    const now = new Date()
    if (b.activeFrom && new Date(b.activeFrom) > now) return false
    if (b.activeTo   && new Date(b.activeTo)   < now) return false
    return true
}

define('FeaturedSection', ({ initialCategory = '', onCategoryChange } = {}) => {
    const search    = signal('')
    const category  = signal(initialCategory)
    const onlyOpen  = signal(false)
    const onlyPromo = signal(false)
    const sortMode  = signal('top')
    const baseItems = signal([])

    const filtered = signal([])

    function shuffle(arr) {
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]]
        }
        return arr
    }

    effect(() => {
        const base  = baseItems.get()
        const term  = search.get()
        const cat   = category.get()
        const open  = onlyOpen.get()
        const promo = onlyPromo.get()
        const sort  = sortMode.get()

        let list = base.filter(b => {
            if (term  && !b.name.toLowerCase().includes(term) &&
                         !b.category.toLowerCase().includes(term)) return false
            if (cat   && b.categorySlug !== cat) return false
            if (open  && !b.open)  return false
            if (promo && !b.hasPromo) return false
            return true
        })

        if (sort === 'random') {
            list = shuffle([...list])
        } else if (sort === 'top') {
            const dest    = list.filter(b => b.destacado)
            const rest    = list.filter(b => !b.destacado)
            const rated   = rest.filter(b => b._reviewAvg > 0)
            const unrated = rest.filter(b => !b._reviewAvg)
            rated.sort((a, b) => b._reviewAvg - a._reviewAvg || b._reviewCount - a._reviewCount)
            list = [...shuffle(dest), ...rated, ...shuffle(unrated)]
        } else {
            list = [...list].sort((a, b) => {
                if (sort === 'name')    return a.name.localeCompare(b.name)
                if (sort === 'reviews') return (b._reviewCount || 0) - (a._reviewCount || 0)
                return (b._reviewAvg || 0) - (a._reviewAvg || 0)
            })
        }

        filtered.set(list)
    })

    const counter = h('p', { className: 'feat-section__count' })
    effect(() => {
        const n = filtered.get().length
        counter.textContent = `${n} negocio${n !== 1 ? 's' : ''} encontrado${n !== 1 ? 's' : ''}`
    })

    /* Fetch negocios + stats de reseñas */
    Promise.all([
        fetch(`/data/businesses.json?t=${Date.now()}`).then(r => r.json()),
        getReviewStats()
    ]).then(([data, stats]) => {
        const items = (data.items || []).filter(b => b.plan === 'oro' && isActive(b))
        items.forEach(b => {
            const s = getBusinessStats(stats, b.id)
            b._reviewAvg   = s.avg
            b._reviewCount = s.count
        })
        baseItems.set(items)
    }).catch(() => {})

    return h('section', { className: 'feat-section' },
        h('div', { className: 'feat-section__inner' },
            h('div', { className: 'feat-section__head' },
                h('div', { className: 'feat-section__titles' },
                    h('span', { className: 'feat-section__label', innerHTML: uiIcons.star + ' Plan Oro' }),
                    h('h2',   { className: 'feat-section__headline' },
                        'Negocios destacados en Fusagasugá'),
                    h('p', { className: 'feat-section__desc' },
                        'Encuentra los mejores negocios verificados y destacados de Fusagasugá. ' +
                        'Profesionales, productos y servicios de confianza cerca de ti.')
                ),
                counter
            ),
            c('FeaturedFilters', {
                categories: directoryData.categories,
                initialCategory,
                onSearch:   term => search.set(term),
                onCategory: slug => { category.set(slug); if (onCategoryChange) onCategoryChange(slug) },
                onOpen:     val  => onlyOpen.set(val),
                onPromo:    val  => onlyPromo.set(val),
                onSort:     mode => sortMode.set(mode)
            }),
            c('BusinessGrid', { businesses: filtered })
        )
    )
})
